import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ShieldCheckIcon as ShieldCheck, ArrowRightIcon as ArrowRight, CalendarBlankIcon as CalendarBlank, ClockIcon as Clock, WarningIcon as Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { GPS_LIVE_WALK_ENABLED } from "@/lib/featureFlags";

/** Format a YYYY-MM-DD string for a target date offset from today. */
function dateNDaysOut(offset) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function prettyDate(iso) {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Booking flow: pick service → pick date → pick slot → sign venue ack →
 * redirect to Stripe Connect Checkout. The venue ack is recorded on
 * the booking row with the customer's IP + ToS version server-side.
 */
export default function BookingDialog({ open, onOpenChange, provider, services }) {
  const navigate = useNavigate();
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState(dateNDaysOut(1));
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [ack, setAck] = useState(false);
  const [refundAck, setRefundAck] = useState(false);
  // Live-walk GPS consent. Pre-checked ON for walker/sitter bookings
  // (most buyers want the live map — they can uncheck if they don't,
  // and they can always toggle it later from MyBookings). Defaults
  // OFF for other service kinds since it's not relevant.
  const isWalkingOrSitting = ["walker", "sitter"].includes(provider?.kind);
  const isFieldHost = provider?.kind === "field_host";
  const [fieldWaiverAck, setFieldWaiverAck] = useState(false);

  const [liveWalkConsent, setLiveWalkConsent] = useState(false);
  // Sync the default once we know the provider kind. We don't want a
  // stale default if the dialog mounts before provider data lands.
  // Force OFF when the feature is parked so we don't persist a stale
  // consent on bookings the user never saw a consent UI for.
  useEffect(() => {
    setLiveWalkConsent(GPS_LIVE_WALK_ENABLED && isWalkingOrSitting);
  }, [isWalkingOrSitting]);
  const [creating, setCreating] = useState(false);
  // Which dogs is this booking for? REQUIRED — every dog coming along
  // must be registered on Armstrong K9 Registry. Powers the Care
  // Passport handoff, vaccination visibility, and heat alerts.
  const [myDogs, setMyDogs] = useState([]);
  const [dogIds, setDogIds] = useState([]);
  const dogId = dogIds[0] || "";
  const maxDogs = isFieldHost ? Number(provider?.land?.max_dogs || 20) : 12;
  const toggleDog = (id) => {
    setDogIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxDogs) {
        toast.error(`This ${isFieldHost ? "field" : "provider"} allows up to ${maxDogs} dogs per visit.`);
        return prev;
      }
      return [...prev, id];
    });
  };

  // Heat-cycle awareness: warn when any attached female dog is (or is
  // predicted to be) in heat on the booked date. Cycles live on the dog
  // doc (heat_cycles: [{start_date, end_date}]); a cycle without an end
  // date is assumed to run 21 days. Prediction: average interval between
  // recorded cycles (single cycle → 180 days).
  const heatWarning = React.useMemo(() => {
    if (!dogIds.length || !pickedSlot) return null;
    const DAY = 86400000;
    const when = new Date(pickedSlot.start_at);
    let result = null;
    for (const id of dogIds) {
      const dog = myDogs.find((d) => d.id === id);
      if (!dog || (dog.sex || "").toLowerCase() !== "female") continue;
      const cycles = (dog.heat_cycles || [])
        .filter((c) => c.start_date)
        .map((c) => {
          const s = new Date(c.start_date);
          const e = c.end_date ? new Date(c.end_date) : new Date(s.getTime() + 21 * DAY);
          return { s, e };
        })
        .sort((a, b) => a.s - b.s);
      if (!cycles.length) continue;
      let hit = null;
      for (const c of cycles) {
        if (when >= c.s && when <= c.e) hit = "active";
      }
      if (!hit) {
        let interval = 180 * DAY;
        if (cycles.length >= 2) {
          let sum = 0;
          for (let i = 1; i < cycles.length; i++) sum += cycles[i].s - cycles[i - 1].s;
          interval = sum / (cycles.length - 1);
        }
        let ps = cycles[cycles.length - 1].s.getTime() + interval;
        while (ps + 21 * DAY < when.getTime()) ps += interval;
        if (when.getTime() >= ps - 3 * DAY && when.getTime() <= ps + 21 * DAY) hit = "predicted";
      }
      if (hit === "active") return { status: "active", name: dog.name };
      if (hit === "predicted" && !result) result = { status: "predicted", name: dog.name };
    }
    return result;
  }, [dogIds, pickedSlot, myDogs]);
  const [passports, setPassports] = useState({});
  // Recurring booking state
  const [sessionsCount, setSessionsCount] = useState(4);
  const [cadence, setCadence] = useState("weekly");
  // International direct-charge providers: buyer must explicitly
  // acknowledge the provider is the merchant of record (no escrow).
  const [paymentModel, setPaymentModel] = useState("platform");
  const [directAck, setDirectAck] = useState(false);

  const service = useMemo(
    () => (services || []).find((s) => s.id === serviceId) || null,
    [serviceId, services],
  );

  const isRecurring = service?.kind === "recurring" && service?.recurring_config;
  const isDirect = paymentModel === "direct";
  // Recurring packages need platform-side off-session charging —
  // unavailable for direct-charge (international) providers.
  const effectiveRecurring = isRecurring && !isDirect;
  const rc = service?.recurring_config || {};
  const availableCadences = (rc.cadences && rc.cadences.length ? rc.cadences : ["weekly"]);
  const maxSessions = Number(rc.max_sessions || 12);

  // Pick the best provider-set discount for this sessions_count.
  const discountPct = useMemo(() => {
    const tiers = rc.discount_tiers || {};
    let best = 0;
    Object.entries(tiers).forEach(([k, v]) => {
      const minN = parseInt(k, 10);
      if (!Number.isNaN(minN) && sessionsCount >= minN && Number(v) > best) best = Number(v);
    });
    return best;
  }, [rc.discount_tiers, sessionsCount]);

  const perSessionPrice = service ? Number(service.price) * (1 - discountPct / 100) : 0;
  // Buyer protection fee — flat 3% added to the customer's bill, funding
  // insurance verification, dispute mediation, and the venue moat.
  // Provider's payout is NOT affected — this is purely customer-side.
  const BUYER_FEE_PCT = 0.03;
  const perSessionBuyerFee = perSessionPrice * BUYER_FEE_PCT;
  const perSessionTotal = perSessionPrice + perSessionBuyerFee;
  const sessionsMultiplier = effectiveRecurring ? sessionsCount : 1;
  const subtotalPrice = perSessionPrice * sessionsMultiplier;
  const totalBuyerFee = perSessionBuyerFee * sessionsMultiplier;
  const grandTotal = subtotalPrice + totalBuyerFee;

  // Reset transient state whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setServiceId(services?.[0]?.id || "");
      setDate(dateNDaysOut(1));
      setPickedSlot(null);
      setNotes("");
      setAck(false);
      setRefundAck(false);
      setFieldWaiverAck(false);
      setSessionsCount(4);
      setCadence("weekly");
      setDirectAck(false);
      if (provider?.user_id) {
        api.get(`/connect/by-user/${provider.user_id}/can-receive`, { silent: true })
          .then((r) => setPaymentModel(r.data?.payment_model === "direct" ? "direct" : "platform"))
          .catch(() => setPaymentModel("platform"));
      }
      api.get("/dogs").then((r) => {
        const dogs = r.data || [];
        setMyDogs(dogs);
        setDogIds(dogs.length === 1 ? [dogs[0].id] : []);
      }).catch(() => setMyDogs([]));
      api.get("/passports/mine").then((r) => setPassports(r.data || {})).catch(() => {});
    }
  }, [open, services]);

  // When a recurring service is selected, ensure cadence is valid.
  useEffect(() => {
    if (isRecurring && !availableCadences.includes(cadence)) {
      setCadence(availableCadences[0]);
    }
  }, [serviceId, isRecurring, availableCadences, cadence]);

  useEffect(() => {
    if (!service || !date || !provider?.id) {
      setSlots([]);
      return;
    }
    let cancelled = false;
    setSlotsLoading(true);
    setPickedSlot(null);
    api
      .get(`/providers/${provider.id}/slots`, {
        params: { service_id: service.id, date },
      })
      .then((r) => { if (!cancelled) setSlots(r.data || []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [service, date, provider?.id]);

  const book = async () => {
    if (!service || !pickedSlot || !ack || !refundAck) return;
    if (!dogIds.length) {
      toast.error("Attach your registered dog(s) to book — every dog coming along must be registered.");
      return;
    }
    if (isFieldHost && !fieldWaiverAck) {
      toast.error("Please sign the Field Use Waiver to book this visit.");
      return;
    }
    if (isDirect && !directAck) {
      toast.error("Please acknowledge the direct-payment terms for this international provider.");
      return;
    }
    setCreating(true);
    try {
      let r;
      if (effectiveRecurring && sessionsCount >= 2) {
        r = await api.post("/bookings/series", {
          service_id: service.id,
          sessions_count: sessionsCount,
          cadence,
          first_scheduled_at: pickedSlot.start_at,
          customer_notes: notes,
          venue_ack: true,
          refund_policy_ack: true,
          field_use_waiver_ack: isFieldHost ? fieldWaiverAck : false,
          live_walk_consent: isWalkingOrSitting ? liveWalkConsent : false,
          dog_id: dogIds[0] || null,
          dog_ids: dogIds,
        });
      } else {
        r = await api.post("/bookings", {
          service_id: service.id,
          scheduled_at: pickedSlot.start_at,
          customer_notes: notes,
          venue_ack: true,
          refund_policy_ack: true,
          field_use_waiver_ack: isFieldHost ? fieldWaiverAck : false,
          live_walk_consent: isWalkingOrSitting ? liveWalkConsent : false,
          dog_id: dogIds[0] || null,
          dog_ids: dogIds,
          direct_payment_ack: isDirect ? directAck : false,
        });
      }
      // Redirect to Stripe Connect Checkout.
      if (r.data?.checkout_url) {
        track("booking_started", { provider_kind: provider?.kind, recurring: effectiveRecurring && sessionsCount >= 2 });
        window.location.href = r.data.checkout_url;
      } else {
        toast.success("Booking created");
        navigate("/my-bookings");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("hasn't completed payment setup")) {
        toast.error(detail, { duration: 8000 });
      } else if (err?.response?.status === 401) {
        toast.error("Please sign in to book this service");
        navigate("/login?next=" + encodeURIComponent(window.location.pathname));
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setCreating(false);
    }
  };

  const dateOptions = useMemo(() => Array.from({ length: 14 }, (_, i) => dateNDaysOut(i + 1)), []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">Book with {provider?.business_name}</DialogTitle>
          <DialogDescription>
            Choose a service and time. Payment is handled by Stripe; funds go directly to the provider.
          </DialogDescription>
        </DialogHeader>

        {(!services || services.length === 0) ? (
          <div className="text-sm text-[#6B6B6B] py-6">
            This provider hasn't published bookable services yet.
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Service */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-2 block">Service</Label>
              <div className="grid sm:grid-cols-2 gap-2">
                {services.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setServiceId(s.id)}
                    data-testid={`book-service-${s.id}`}
                    className={`text-left p-3 rounded-lg border transition-colors ${
                      serviceId === s.id
                        ? "border-gold bg-cream"
                        : "border-[#EAEAEA] hover:border-gold/55"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-ink text-sm">{s.name}</span>
                      <span className="font-tabular font-semibold text-ink text-sm">${s.price.toFixed(2)}</span>
                    </div>
                    <p className="text-[11.5px] text-[#6B6B6B] mt-0.5">
                      {s.duration_minutes} min{s.kind === "recurring" ? " · recurring" : ""}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Recurring config — sessions + cadence */}
            {isRecurring && isDirect && (
              <p className="text-[11.5px] text-[#6B6B6B] bg-[#FAFAFA] border border-[#EAEAEA] rounded-lg p-3" data-testid="direct-no-recurring-note">
                Recurring packages aren't available for international providers —
                this will be booked as a single session.
              </p>
            )}
            {effectiveRecurring && (
              <div className="bg-cream/60 border border-gold/35 rounded-lg p-3" data-testid="recurring-config">
                <p className="text-[11.5px] uppercase tracking-wider font-bold text-ink mb-2">Recurring package</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Sessions</Label>
                    <div className="flex gap-1 flex-wrap">
                      {[2, 4, 8, 12].filter((n) => n <= maxSessions).map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setSessionsCount(n)}
                          data-testid={`sessions-${n}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                            sessionsCount === n
                              ? "bg-ink text-white border-ink"
                              : "bg-white border-[#EAEAEA] text-ink hover:border-gold/55"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  {availableCadences.length > 1 && (
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Cadence</Label>
                      <div className="flex gap-1 flex-wrap">
                        {availableCadences.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setCadence(c)}
                            data-testid={`cadence-${c}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border capitalize transition-colors ${
                              cadence === c
                                ? "bg-ink text-white border-ink"
                                : "bg-white border-[#EAEAEA] text-ink hover:border-gold/55"
                            }`}
                          >
                            {c === "biweekly" ? "Bi-weekly" : "Weekly"}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-3 text-[12px] text-[#3A3A3A] leading-relaxed">
                  {discountPct > 0 ? (
                    <span className="text-[#0F8043] font-semibold">{discountPct}% off</span>
                  ) : null}{" "}
                  {discountPct > 0 ? "applied — " : ""}
                  <strong className="font-tabular">${perSessionPrice.toFixed(2)}</strong>/session × {sessionsCount} = <strong className="font-tabular">${subtotalPrice.toFixed(2)}</strong>
                </div>
                <p className="text-[11px] text-[#6B6B6B] mt-1.5 leading-snug">
                  Pay <strong>${perSessionTotal.toFixed(2)}</strong> now for session 1 (includes ${perSessionBuyerFee.toFixed(2)} buyer protection). The remaining {sessionsCount - 1} sessions will be charged to the same card 24h before each session. Cancel any session up to 24h ahead for a full refund.
                </p>
              </div>
            )}

            {/* Which dogs — REQUIRED. Every dog coming along must be
                registered so the pro gets the Care Passport, vaccination
                records, and heat alerts. */}
            {myDogs.length > 0 ? (
              <div data-testid="booking-dog-picker">
                <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-2 block">
                  Which dog{myDogs.length > 1 ? "s" : ""}? <span className="text-crimson">*</span>
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {myDogs.slice(0, 24).map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => toggleDog(d.id)}
                      data-testid={`book-dog-${d.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        dogIds.includes(d.id) ? "bg-ink text-white border-ink" : "bg-white border-[#EAEAEA] text-ink hover:border-gold/55"
                      }`}
                    >
                      {d.name}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-[#6B6B6B] mt-1.5" data-testid="booking-dog-benefits">
                  Every dog you bring must be registered. Registered dogs get a Care Passport your pro can view, vaccination records, and heat alerts.
                </p>
                {dogIds.length === 0 && (
                  <p className="text-[11.5px] font-semibold text-crimson mt-1" data-testid="booking-dog-required-note">
                    Select the dog{myDogs.length > 1 ? "(s)" : ""} coming to this booking.
                  </p>
                )}
                {dogId && provider?.kind === "trainer" && (passports[dogId]?.training ?? 0) < 50 ? (
                  <a
                    href={`/dogs/${dogId}/passport?section=training`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 flex items-center justify-between gap-2 text-[12px] font-semibold text-[#1F5FBF] bg-[#EFF5FD] hover:bg-[#E0EBFA] border border-[#B9D2F2] rounded-lg px-3 py-2 transition-colors"
                    data-testid="booking-training-passport-prompt"
                  >
                    <span>
                      {(passports[dogId]?.training ?? 0) === 0
                        ? `${myDogs.find((d) => d.id === dogId)?.name}'s Training & Behavior section is empty`
                        : `${myDogs.find((d) => d.id === dogId)?.name}'s Training & Behavior section is only ${passports[dogId]?.training}% complete`} —
                      fill it so {provider?.business_name} can plan the right session (~1 min)
                    </span>
                    <ArrowRight size={13} weight="bold" className="shrink-0" />
                  </a>
                ) : dogId && (passports[dogId]?.overall ?? 0) < 70 && (
                  <a
                    href={`/dogs/${dogId}/passport`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 flex items-center justify-between gap-2 text-[12px] font-semibold text-[#8A6A00] bg-[#FBF7EC] hover:bg-[#F5EBCC] border border-[#E5D08A] rounded-lg px-3 py-2 transition-colors"
                    data-testid="booking-passport-prompt"
                  >
                    <span>
                      {myDogs.find((d) => d.id === dogId)?.name}'s Care Passport is {passports[dogId]?.overall ?? 0}% complete —
                      finish it so {provider?.business_name} can prepare (~1 min)
                    </span>
                    <ArrowRight size={13} weight="bold" className="shrink-0" />
                  </a>
                )}
              </div>
            ) : (
              <div className="rounded-xl border-2 border-gold/50 bg-cream p-4" data-testid="booking-register-dog-gate">
                <p className="text-[13px] font-semibold text-ink mb-1">Register your dog to book</p>
                <p className="text-[12px] text-[#6B6B6B] leading-relaxed mb-3">
                  Every dog coming to a booking must be registered on Armstrong K9 Registry.
                  Registered dogs get a Care Passport your pro can view, vaccination records,
                  heat alerts, and a permanent registration number.
                </p>
                <a
                  href="/dogs/new"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full bg-ink hover:bg-crimson text-white text-[12.5px] font-bold px-5 py-2.5 transition-colors"
                  data-testid="booking-register-dog-cta"
                >
                  Register a dog <ArrowRight size={13} weight="bold" />
                </a>
                <button
                  type="button"
                  onClick={() => api.get("/dogs").then((r) => { const dogs = r.data || []; setMyDogs(dogs); setDogIds(dogs.length === 1 ? [dogs[0].id] : []); }).catch(() => {})}
                  className="ml-3 text-[12px] font-semibold text-[#8A6A00] underline"
                  data-testid="booking-refresh-dogs"
                >
                  I registered — refresh
                </button>
              </div>
            )}

            {/* Date */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-2 block flex items-center gap-1.5">
                <CalendarBlank size={12} weight="duotone" /> {isRecurring ? "Start date" : "Date"}
              </Label>
              <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="book-dates">
                {dateOptions.map((d) => {
                  const dt = new Date(d + "T12:00:00");
                  const active = d === date;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDate(d)}
                      data-testid={`book-date-${d}`}
                      className={`flex-shrink-0 px-3 py-2 rounded-lg border text-center transition-colors ${
                        active ? "border-gold bg-cream" : "border-[#EAEAEA] hover:border-gold/55"
                      }`}
                    >
                      <div className="text-[10px] uppercase text-[#6B6B6B] tracking-wider">
                        {dt.toLocaleDateString(undefined, { weekday: "short" })}
                      </div>
                      <div className="font-semibold text-ink text-sm leading-tight">
                        {dt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Slots */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-2 block flex items-center gap-1.5">
                <Clock size={12} weight="duotone" /> Available times
              </Label>
              {slotsLoading ? (
                <div className="text-xs text-[#6B6B6B]">Loading slots…</div>
              ) : slots.length === 0 ? (
                <div className="text-xs text-[#6B6B6B] bg-[#FAFAFA] border border-[#EAEAEA] rounded-lg p-3" data-testid="no-slots">
                  No openings on this date. Try another day, or contact the provider directly to discuss.
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5" data-testid="slot-grid">
                  {slots.map((s) => {
                    const active = pickedSlot?.start_at === s.start_at;
                    return (
                      <button
                        key={s.start_at}
                        type="button"
                        onClick={() => setPickedSlot(s)}
                        data-testid={`slot-${s.start_at}`}
                        className={`px-2 py-2 rounded-lg border text-[12.5px] font-medium transition-colors ${
                          active ? "border-gold bg-cream text-ink" : "border-[#EAEAEA] hover:border-gold/55"
                        }`}
                      >
                        {new Date(s.start_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-1.5 block">
                Notes for the provider (optional)
              </Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
                placeholder="Anything they should know — dog's name, breed, allergies, gate code…"
                data-testid="booking-notes"
              />
            </div>

            {/* International direct-payment disclosure — required opt-in */}
            {isDirect && (
              <div className="rounded-xl border-2 border-[#E5A33B]/60 bg-[#FFF8EC] p-3.5" data-testid="direct-payment-disclosure">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#8A5A00] mb-1.5 flex items-center gap-1.5">
                  <Warning size={13} weight="fill" /> International provider — direct payment
                </p>
                <p className="text-[12px] text-[#3B2A07] leading-relaxed mb-2">
                  This provider is located <strong>outside the United States</strong>.
                  Your payment goes <strong>directly to the provider's own Stripe
                  account</strong> — they are the merchant of record, not Armstrong
                  K9 Registry. The Registry never holds these funds, and{" "}
                  <strong>Registry escrow and dispute protections do not apply</strong>.
                  Refunds, disputes, and chargebacks are handled solely by the provider.
                </p>
                <label className="flex items-start gap-2 cursor-pointer text-[12.5px] text-ink">
                  <input
                    type="checkbox"
                    checked={directAck}
                    onChange={(e) => setDirectAck(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[#8A5A00]"
                    data-testid="direct-payment-ack-checkbox"
                  />
                  <span>
                    I understand and agree that this payment goes directly to the
                    provider with no Registry escrow or dispute protection.
                  </span>
                </label>
              </div>
            )}

            {/* Heat-cycle warning — shown when the attached female is in
                (or predicted to be in) heat on the booked date. */}
            {heatWarning && (
              <div className="rounded-xl border-2 border-[#C1272D]/45 bg-[#FDECEC]/70 p-3.5" data-testid="heat-warning">
                <p className="text-[12.5px] font-semibold text-[#8f1d22] flex items-start gap-2 leading-relaxed">
                  <span className="mt-0.5">⚠</span>
                  <span>
                    {heatWarning.name || "Your dog"}{" "}
                    {heatWarning.status === "active"
                      ? "appears to be in heat on this date based on her recorded cycles."
                      : "may be in heat around this date based on her cycle history."}{" "}
                    {isFieldHost
                      ? "Many Zoomie Time hosts don't allow in-heat females — scent lingers and affects the next guests' dogs. Consider another date or message the host first."
                      : "Many pros have policies about in-heat females — consider another date or message the provider first."}
                  </span>
                </p>
              </div>
            )}

            {/* Field Use Waiver — required for private-field visits.
                Full text shown; server stamps legal_acks.field_use_waiver
                with IP + timestamp + version. */}
            {isFieldHost && (
              <div className="rounded-xl border-2 border-[#2E6B3E]/50 bg-[#F2F8F3] p-3.5" data-testid="field-waiver-wrap">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#2E6B3E] mb-1.5">
                  Field Use Waiver &amp; Release — required
                </p>
                <div className="text-[11.5px] text-[#28402D] leading-relaxed mb-2 max-h-36 overflow-y-auto pr-1 space-y-1.5" data-testid="field-waiver-text">
                  <p>By booking this private field visit I certify that:</p>
                  <p>1. Every dog I bring is current on <strong>rabies and DHPP/DA2PP vaccinations</strong> (or current titers) and on veterinarian-recommended flea and parasite preventatives.</p>
                  <p>2. I will <strong>not</strong> bring unattended dogs, dogs with a bite history, or dangerously aggressive dogs.</p>
                  <p>3. I will supervise my dogs at all times, follow the host's posted rules, clean up after my dogs, and leave the property as I found it.</p>
                  <p>I understand the property, its fencing, terrain, and safety details are <strong>self-reported by the host and NOT inspected or verified by Armstrong K9 Registry</strong>. I <strong>voluntarily assume all risk</strong> of injury, illness, death, or property damage to myself, my guests, and my dogs. I <strong>release, waive, and hold harmless both the property host and Armstrong K9 Registry</strong>, its owners, and affiliates from any and all claims arising out of this visit. I am legally and financially responsible for any injury or damage caused by me, my guests, or my dogs.</p>
                </div>
                <label className="flex items-start gap-2 cursor-pointer text-[12.5px] text-ink">
                  <input
                    type="checkbox"
                    checked={fieldWaiverAck}
                    onChange={(e) => setFieldWaiverAck(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[#2E6B3E]"
                    data-testid="field-waiver-ack-checkbox"
                  />
                  <span><strong>I have read and agree to the Field Use Waiver &amp; Release above.</strong></span>
                </label>
                <p className="text-[11px] text-[#4A5F50] mt-2" data-testid="field-vax-tip">
                  Tip: attach your dog above and add vaccination records to their Care Passport — the host can view them before your visit.
                </p>
              </div>
            )}

            {/* Venue moat ack */}
            <div className="rounded-xl border border-[#EAEAEA] bg-[#FAFAFA] p-3 flex items-start gap-3">
              <Checkbox
                id="venue-ack"
                checked={ack}
                onCheckedChange={(c) => setAck(!!c)}
                data-testid="booking-venue-ack"
              />
              <Label htmlFor="venue-ack" className="text-[12.5px] text-ink leading-relaxed cursor-pointer">
                <ShieldCheck size={12} weight="duotone" className="inline mr-1 -mt-0.5 text-[#0F8043]" />
                I understand this service is provided by <strong>{provider?.business_name}</strong>, an independent business. Armstrong K9 Registry is the venue connecting us — it is not a party to this transaction and is not responsible for the service performed.
              </Label>
            </div>

            {/* Refund-policy click-wrap — Armstrong does not refund or
                mediate refund disputes on service bookings. Required on
                every paid booking. Server stamps `legal_acks.refund_policy_for_service_bookings`
                with IP + timestamp + version v2026-06-02. */}
            <div className="rounded-xl border border-crimson/40 bg-[#FDECEC]/60 p-3 flex items-start gap-3" data-testid="booking-refund-ack-wrap">
              <Checkbox
                id="refund-policy-ack"
                checked={refundAck}
                onCheckedChange={(c) => setRefundAck(!!c)}
                data-testid="booking-refund-policy-ack"
              />
              <Label htmlFor="refund-policy-ack" className="text-[12.5px] text-ink leading-relaxed cursor-pointer">
                <strong>I acknowledge the Refund Policy.</strong>{" "}
                Armstrong K9 Registry <strong>does not issue refunds and does not mediate refund disputes</strong> on
                service-provider bookings. {provider?.business_name || "The provider"} is the merchant of record;
                refunds are governed by their published cancellation policy. If we can't reach a
                resolution, my recourse is a Stripe chargeback through my card issuer or
                small-claims court — not Armstrong. The 12% platform fee is non-refundable.{" "}
                <a
                  href="/refund#service-provider-bookings"
                  target="_blank"
                  rel="noreferrer"
                  className="underline font-semibold"
                  onClick={(e) => e.stopPropagation()}
                >
                  Read the full policy →
                </a>
              </Label>
            </div>

            {/* Live walk GPS consent — only for walker/sitter bookings.
                Pre-checked ON because it's the value-prop people booked
                a walker for in the first place, but easy to opt-out and
                toggleable later from MyBookings. */}
            {GPS_LIVE_WALK_ENABLED && isWalkingOrSitting && (
              <div className="rounded-xl border border-[#EAEAEA] bg-[#FAFAFA] p-3 flex items-start gap-3" data-testid="booking-live-walk-consent-wrap">
                <Checkbox
                  id="live-walk-consent"
                  checked={liveWalkConsent}
                  onCheckedChange={(c) => setLiveWalkConsent(!!c)}
                  data-testid="booking-live-walk-consent"
                />
                <Label htmlFor="live-walk-consent" className="text-[12.5px] text-ink leading-relaxed cursor-pointer">
                  <strong>Allow live walk tracking.</strong> The provider can share a
                  live GPS map of the walk with me. Pickup and drop-off locations
                  on the public map are obscured to ~110m to protect my address.
                  I can revoke this any time from <em>My Bookings</em>; the share
                  link auto-expires 2 hours after the walk ends.
                </Label>
              </div>
            )}

            {pickedSlot && service && (
              <div className="rounded-xl border border-gold/35 bg-cream p-3 text-[12.5px] text-ink" data-testid="booking-summary">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-semibold">{service.name}</p>
                    <p className="text-[#6B6B6B]">
                      {isRecurring
                        ? `Starts ${prettyDate(pickedSlot.start_at)} · ${sessionsCount} sessions, ${cadence === "biweekly" ? "every 2 weeks" : "weekly"}`
                        : `${prettyDate(pickedSlot.start_at)} · ${service.duration_minutes} min`}
                    </p>
                  </div>
                </div>
                <div className="mt-2.5 pt-2.5 border-t border-gold/25 space-y-1 text-[12px]">
                  <Line
                    label={isRecurring ? `Service (${sessionsCount} session${sessionsCount === 1 ? "" : "s"})` : "Service"}
                    value={`$${subtotalPrice.toFixed(2)}`}
                  />
                  <Line
                    label="Buyer protection fee"
                    value={`$${totalBuyerFee.toFixed(2)}`}
                    tooltip="Funds insurance verification, background-check infrastructure, dispute mediation, and the platform's role as a neutral venue. 3% of the service price."
                    testid="buyer-protection-line"
                  />
                  <Line
                    label={isRecurring ? "Due today (session 1)" : "Total"}
                    value={isRecurring ? `$${perSessionTotal.toFixed(2)}` : `$${grandTotal.toFixed(2)}`}
                    bold
                  />
                  {isRecurring && (
                    <p className="text-[10.5px] text-[#6B6B6B] mt-1.5 leading-snug">
                      Sessions 2–{sessionsCount} auto-charged 24h before each at <strong>${perSessionTotal.toFixed(2)}</strong>. Series total: <strong className="font-tabular">${grandTotal.toFixed(2)}</strong>.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-row items-center gap-2 justify-between">
          <p className="text-[11px] text-[#6B6B6B] flex items-center gap-1">
            <Warning size={11} weight="duotone" /> Free cancel up to 24h before
          </p>
          <Button
            onClick={book}
            disabled={!service || !pickedSlot || !ack || !refundAck || !dogIds.length || (isFieldHost && !fieldWaiverAck) || (isDirect && !directAck) || creating}
            className="rounded-full bg-gold-gradient hover:opacity-95 text-ink font-semibold border-0"
            data-testid="booking-confirm-btn"
          >
            {creating ? "Redirecting…" : <>Continue to payment <ArrowRight size={13} weight="bold" className="ml-1" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Line({ label, value, tooltip, bold, testid }) {
  return (
    <div className="flex items-center justify-between gap-2" data-testid={testid}>
      <span className={`${bold ? "font-semibold text-ink" : "text-[#6B6B6B]"} inline-flex items-center gap-1`}>
        {label}
        {tooltip && (
          <span title={tooltip} className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[9px] font-bold text-[#6B6B6B] border border-[#D8D5CC] cursor-help" aria-label="What's this?">
            ?
          </span>
        )}
      </span>
      <span className={`font-tabular ${bold ? "font-semibold text-ink" : "text-ink"}`}>{value}</span>
    </div>
  );
}
